import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cguWriteErrorMessage } from '@/api/legal-admin-client';
import { useCguVersions, useCguVersion, useSaveCguDraft } from '@/api/use-legal-admin';
import { CguEditor } from '@/routes/admin/cgu/cgu-editor';

// =============================================================================
// cgu-draft-create.tsx — Création d'un BROUILLON de CGU (A-CGU ÉTAPE 2, onglet b, mode « nouveau »).
// =============================================================================
// Formulaire de création : numéro fonctionnel (CGU-YYYY-MM), changelog, date d'entrée en vigueur
// (optionnelle), + éditeur du corps HTML. POST /legal/cgu/versions (admin, admin_csrf). L'autorité
// (unicité du numéro, « un seul brouillon actif », nettoyage du HTML) reste l'API — l'erreur remonte.
//
// Le corps `contentHtml` composé ici est ENVOYÉ au back (ca0aa8b) qui le PERSISTE et le NETTOIE
// (sanitiseur allowlist serveur — le front ne sanitise pas). Un contenu aberrant → 400 : on affiche
// alors un message générique (jamais le message serveur brut, via `cguWriteErrorMessage`).

export interface CguDraftCreateProps {
  /** Notifie le parent après création (sélection du brouillon créé). */
  readonly onCreated: (cguVersionId: string) => void;
}

const EMPTY_BODY = '<h1>Conditions Générales</h1>\n<p></p>';
/** Numéro par défaut CGU-AAAA-MM (année+mois courants), modifiable — DBU l.2026. */
function currentCguNumber(now: Date = new Date()): string {
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `CGU-${yyyy}-${mm}`;
}

export function CguDraftCreate({ onCreated }: CguDraftCreateProps): React.JSX.Element {
  const save = useSaveCguDraft();
  const versions = useCguVersions();
  const publishedId = versions.data?.find((v) => v.state === 'Publiee')?.cguVersionId;
  const published = useCguVersion(publishedId);
  const onDuplicate = (): void => {
    if (published.data) {
      setBody(published.data.contentHtml ?? EMPTY_BODY);
    }
  };
  const [versionNumber, setVersionNumber] = React.useState(currentCguNumber());
  const [changelog, setChangelog] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState('');
  const [body, setBody] = React.useState(EMPTY_BODY);

  const canSubmit = versionNumber.trim().length > 0 && changelog.trim().length > 0;

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    save.mutate(
      {
        versionNumber: versionNumber.trim(),
        changelog: changelog.trim(),
        ...(effectiveDate !== '' ? { effectiveDate: new Date(effectiveDate).toISOString() } : {}),
        contentHtml: body,
      },
      { onSuccess: (result) => onCreated(result.cguVersionId) },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">Nouveau brouillon</h2>
        <p className="text-sm text-muted-foreground">
          Créez une version en brouillon. Elle sera modifiable puis publiable depuis le
          registre.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="cgu-version-number">Numéro de version</Label>
          <Input
            id="cgu-version-number"
            value={versionNumber}
            onChange={(event) => setVersionNumber(event.target.value)}
            placeholder="CGU-2026-08"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cgu-effective-date">Entrée en vigueur (optionnelle)</Label>
          <Input
            id="cgu-effective-date"
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cgu-changelog">Changelog</Label>
        <Input
          id="cgu-changelog"
          value={changelog}
          onChange={(event) => setChangelog(event.target.value)}
          placeholder="Résumé des évolutions de cette version"
          required
        />
      </div>

      {publishedId !== undefined ? (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={published.isPending}
            onClick={onDuplicate}
          >
            {published.isPending ? 'Chargement…' : 'Dupliquer le contenu de la version publiée'}
          </Button>
          <p className="text-sm text-muted-foreground">
            Évolution mineure : repartez du contenu publié. Sinon, éditeur vide (refonte).
          </p>
        </div>
      ) : null}

      <CguEditor value={body} onChange={setBody} id="cgu-new-draft-content" />

      {save.isError ? (
        <p role="alert" className="text-destructive">
          Création refusée : {cguWriteErrorMessage(save.error, 'erreur inconnue.')}
        </p>
      ) : null}

      <Button type="submit" disabled={!canSubmit || save.isPending}>
        {save.isPending ? 'Création…' : 'Créer le brouillon'}
      </Button>
    </form>
  );
}
